import { loggingUtility } from '@/utils/loggingUtility';
import { apiClient } from './clients/axiosClient';

/**
 * Request for temporary upload URL
 */
export interface RequestTempUploadUrlRequest {
  filename: string;
  mimeType: string;
  contentLength: number;
  projectId?: string;
}

/**
 * Response for temporary upload URL
 */
export interface RequestTempUploadUrlResponse {
  success: boolean;
  uploadUrl: string;
  tempKey: string;
  expiresAt: string;
  maxBytes: number;
}

/**
 * Request for temporary GET URL
 */
export interface RequestTempGetUrlRequest {
  tempKey: string;
}

/**
 * Response for temporary GET URL
 */
export interface RequestTempGetUrlResponse {
  success: boolean;
  getUrl: string;
  expiresAt: string;
}

/**
 * Service for S3 image operations
 */
export const imageApi = {
  /**
   * Request a presigned PUT URL for uploading an image to S3 temporary storage
   */
  async requestTempUploadUrl(
    request: RequestTempUploadUrlRequest
  ): Promise<RequestTempUploadUrlResponse> {
    try {
      const response = await apiClient.post<RequestTempUploadUrlResponse>(
        '/surveys/images/temp-upload-url',
        request
      );
      return response;
    } catch (error) {
      loggingUtility.error('Error requesting temp upload URL:', error);
      throw error;
    }
  },

  /**
   * Request a presigned GET URL for downloading an image from S3 temporary storage
   */
  async requestTempGetUrl(
    request: RequestTempGetUrlRequest
  ): Promise<RequestTempGetUrlResponse> {
    try {
      const response = await apiClient.post<RequestTempGetUrlResponse>(
        '/surveys/images/temp-get-url',
        request
      );
      return response;
    } catch (error) {
      loggingUtility.error('Error requesting temp GET URL:', error);
      throw error;
    }
  },

  /**
   * Upload a file directly to S3 using a presigned PUT URL
   */
  async uploadToS3(
    file: File,
    uploadUrl: string,
    mimeType: string
  ): Promise<void> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': mimeType,
        },
      });

      if (!response.ok) {
        throw new Error(
          `S3 upload failed: ${response.status} ${response.statusText}`
        );
      }
    } catch (error) {
      loggingUtility.error('Error uploading file to S3:', error);
      throw error;
    }
  },
};
